import React from 'react';
import { Badge, BadgeSize, BadgeVariant } from '@usepepper/ui/atoms/badge';
import { createStyles, useTheme } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { getTranslationValue } from '@/utils/translations';
import { HubCardBase } from '@/components/insights-hub/hub-card-base';
import { HubMonthlyCommissionSkeleton } from '@/components/insights-hub/hub-monthly-commission-skeleton';
import { Platform, Text, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useCanViewMonthlyCommission } from '@/hooks/insights-hub/use-monthly-commission-enabled';
import { useMonthlyCommissionData } from '@/hooks/insights-hub/use-monthly-commission-data';

const LARGE_DISPLAY_MONEY_UPPER_BOUND = 1e12;

export const HubMonthlyCommission: React.FC = () => {
  const theme = useTheme();
  const [styles] = useStyles();

  const isMonthlyCommissionEnabled = useCanViewMonthlyCommission();

  const { data, error, loading } = useMonthlyCommissionData({
    skip: !isMonthlyCommissionEnabled,
  });

  // Gating on feature flag
  if (!isMonthlyCommissionEnabled) {
    return null;
  }
  // cache policy is cache-first
  if (loading && !data) {
    return Platform.OS === 'web' ? <HubMonthlyCommissionSkeleton /> : null;
  }

  if (!data || error) {
    return null;
  }

  const currentCommissionInMicros =
    data.current_month_commission_in_micros || 0;

  return (
    <>
      <HubCardBase
        description={getTranslationValue(data.subtitle)}
        title={getTranslationValue(data.title)}
      >
        <View style={styles.container}>
          <View style={styles.content}>
            <Text
              style={[
                styles.mainQuantity,
                // if value too large (>$1M), reduce font size
                currentCommissionInMicros > LARGE_DISPLAY_MONEY_UPPER_BOUND
                  ? styles.mainQuantityTooLong
                  : null,
              ]}
            >
              {formatCurrency(currentCommissionInMicros, data.currency_code)}
            </Text>
          </View>
          <View style={styles.content}>
            <Badge
              label={
                data.previous_month_commission_in_micros
                  ? formatCurrency(
                      data.previous_month_commission_in_micros,
                      data.currency_code,
                    )
                  : '--'
              }
              size={BadgeSize.MEDIUM}
              variant={BadgeVariant.DEFAULT}
            />
            <View collapsable={false}>
              <Typography
                color={theme.sk.color.text.secondary}
                variant="paragraph-md"
              >
                {getTranslationValue(data.previous_period)}
              </Typography>
            </View>
          </View>
        </View>
      </HubCardBase>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    gap: theme.sk.spacing.spaceSmall,
    justifyContent: 'center',
    marginVertical: theme.sk.spacing.spaceXlarge,
    width: '100%',
  },
  content: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'center',
  },
  mainQuantity: {
    fontFamily: theme.sk.typography.heading['2xl'].fontFamily,
    fontSize: theme.sk.typography.heading['2xl'].fontSize,
  },
  mainQuantityTooLong: {
    fontFamily: theme.sk.typography.heading['2xl'].fontFamily,
    fontSize: 32, // font size not available in design system.
  },
}));
